import { getAuth } from "firebase/auth";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { apiClient, AmenityTableLoaderResult } from "~/services/apiClient";
import { LoaderData, PrecomputedAmenityData } from "~/types";
import dayjs from "dayjs";

/** Transform Lambda response (ISO strings) to PrecomputedAmenityData (dayjs) */
function transformAmenityItem(raw: AmenityTableLoaderResult["amenityList"][0]): PrecomputedAmenityData {
  return {
    formattedAmenityDetails: {
      ...raw.formattedAmenityDetails,
      reservations: raw.formattedAmenityDetails.reservations.map((r) => ({
        timeFrame: {
          start: dayjs(r.timeFrame.start),
          end: dayjs(r.timeFrame.end),
        },
        createdAt: dayjs(r.createdAt),
        data: r.data,
      })),
    },
    availability: {
      available: (() => {
        const days = raw.availability.available.slice(0, 7);
        while (days.length < 7) days.push([]);
        return days.map((day) =>
          day.map((tf) => ({
            start: dayjs(tf.start),
            end: dayjs(tf.end),
          }))
        ) as PrecomputedAmenityData["availability"]["available"];
      })(),
      unavailable: raw.availability.unavailable.map((u) => ({
        timeFrame: {
          start: dayjs(u.timeFrame.start),
          end: dayjs(u.timeFrame.end),
        },
        reason: u.reason,
      })),
      fullyUnavailable: raw.availability.fullyUnavailable,
    },
  };
}

export async function AmenityTablePageLoader({
  request,
}: LoaderFunctionArgs): Promise<AmenityTablePageLoaderType | Response> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return redirect("/explore");

  const { searchParams } = new URL(request.url);
  const propertyID = searchParams.get("propertyID") ?? searchParams.get("id") ?? "";

  if (!propertyID) {
    return {
      amenityList: {
        success: false,
        data: [],
        error: new Error("propertyID is required"),
      },
    };
  }

  const response = await apiClient.loader<AmenityTableLoaderResult>(
    "amenity-table",
    { propertyID }
  );

  if (!response.success || !response.data) {
    return {
      amenityList: {
        success: false,
        data: [],
        error: new Error(response.error ?? "Failed to load amenities"),
      },
    };
  }

  const amenityList: LoaderData<PrecomputedAmenityData[]> = {
    success: true,
    data: response.data.amenityList.map(transformAmenityItem),
  };

  return { amenityList };
}

export type AmenityTablePageLoaderType = {
  amenityList: LoaderData<PrecomputedAmenityData[]>;
};
